import { computed, Injectable, Signal, signal } from '@angular/core';
import { NavGroup, NavLink } from '@shared/components/layout/sidebar-content/models/nav-link.model';
import { PermissionConsts } from '@shared/permission-consts';
import { DeviceDetectorService } from 'ngx-device-detector';
import { normalizeString } from '@shared/helpers/string-utils';

@Injectable({ providedIn: 'root' })
export class LayoutStore {
    // Navigation grouped into labelled clusters so the desktop menubar stays scannable
    // (Miller's law) and every destination shows a text label. The flat `navLinks` below
    // is derived from these groups and feeds the mobile drawer.
    readonly navGroups: NavGroup[] = [
        {
            label: 'Operations',
            icon: 'fas fa-briefcase-medical',
            children: [
                {
                    label: 'OperatorDashboard',
                    icon: 'fas fa-calendar-check',
                    route: '/app/dashboard',
                    permission: PermissionConsts.Role_OperatorLike,
                },
                {
                    label: 'Services',
                    icon: 'fas fa-layer-group',
                    route: '/app/services',
                    permission: PermissionConsts.Service_List,
                },
                {
                    label: 'GlobalScheduler',
                    icon: 'fas fa-calendar-alt',
                    route: '/app/global-scheduler',
                    permission: [PermissionConsts.Role_BackOfficeLike, PermissionConsts.Activity_List, PermissionConsts.OperatorActivity_List],
                },
            ],
        },
        {
            label: 'People',
            icon: 'fas fa-user',
            children: [
                {
                    label: 'Staff',
                    icon: 'fas fa-user-tie',
                    route: '/app/users/list/operators',
                    permission: PermissionConsts.User_GetAllOperatorLike,
                },
                {
                    label: 'Beneficiaries',
                    icon: 'fas fa-hand-holding-heart',
                    route: '/app/users/list/beneficiaries',
                    permission: PermissionConsts.User_GetAllBeneficiaryLike,
                },
                {
                    label: 'Administration',
                    icon: 'fas fa-users-cog',
                    route: '/app/users/list/back-office',
                    permission: PermissionConsts.User_GetAllBackOfficeLike,
                },
                {
                    label: 'Roles',
                    icon: 'fas fa-key',
                    route: '/app/roles/list',
                    permission: PermissionConsts.Role_GetAll,
                },
            ],
        },
        {
            label: 'Facilities',
            icon: 'fas fa-building',
            children: [
                {
                    label: 'Structures',
                    icon: 'fas fa-building',
                    route: '/app/structures',
                    permission: PermissionConsts.Structure_GetAll,
                },
                {
                    label: 'Assets',
                    icon: 'fas fa-laptop',
                    route: '/app/assets',
                    permission: PermissionConsts.Asset_GetAll,
                },
                {
                    label: 'VehiclesLot',
                    icon: 'fas fa-car',
                    route: '/app/vehicles-lot',
                    permission: PermissionConsts.Vehicle_GetAll,
                },
            ],
        },
    ];

    readonly navLinks: Partial<NavLink>[] = this.navGroups.flatMap((group) => group.children);

    isMobileScreen = computed<boolean>(() => this._isMobileWidth() || this.deviceDetectorService.isMobile());

    private readonly _mobileMaxWidthPx = 768;
    private _sidebarExpanded = signal<boolean>(false);
    // Seeded from the current width: the resize listener doesn't fire on first load, so a page opened straight from mobile would claim to be desktop.
    private _isMobileWidth = signal<boolean>(window.innerWidth < this._mobileMaxWidthPx);
    private _logoPath = signal<string>(null);
    private _recordNavCollapsed = signal<boolean>(false);

    constructor(private deviceDetectorService: DeviceDetectorService) {
        window.addEventListener('resize', () => {
            this._isMobileWidth.set(window.innerWidth < this._mobileMaxWidthPx);
        });
    }

    get sidebarExpanded(): Signal<boolean> {
        return this._sidebarExpanded.asReadonly();
    }

    set sidebarExpanded(value: boolean) {
        this._sidebarExpanded.set(value);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get logoPath(): Signal<string> {
        return this._logoPath.asReadonly();
    }

    set logoPath(value: string) {
        this._logoPath.set(value);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get isMobileWidth(): Signal<boolean> {
        return this._isMobileWidth.asReadonly();
    }

    // Collapse state for the intra-record sub-rail; kept here (root) so it persists as the
    // operator moves between records and widgets during a shift.
    // eslint-disable-next-line @typescript-eslint/member-ordering
    get recordNavCollapsed(): Signal<boolean> {
        return this._recordNavCollapsed.asReadonly();
    }

    switchSidebar(): void {
        this._sidebarExpanded.update((value) => !value);
    }

    toggleRecordNav(): void {
        this._recordNavCollapsed.update((value) => !value);
    }

    setTenantLogoPath(tenantName: string, logoName: string): void {
        this.logoPath = `assets/logo/${normalizeString(tenantName)}/${logoName}`;
    }

    setGlobalFont(fontClass: string): void {
        document.body.classList.add(fontClass);
    }
}
